import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/system';

export const ECSDKPlayer: React.FC<ECSDKPlayerProps> = ({
  partnerKey,
  accessToken,
  dialogId,
  container = 'player-container',
  languageCode,
  autoStart = false,
  isComprehensionQuizEnabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const ErrorContainer = styled(Box)(({ theme }) => ({
    textAlign: 'center',
    padding: theme.spacing(2.5),
    backgroundColor: '#F5F7F9',
    borderRadius: theme.shape.borderRadius,
    border: '1px solid #CFD6DD',
    margin: `${theme.spacing(1.25)} 0`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }));

  useEffect(() => {
    let isPlayerReady = false;
    let readyHandlerTimeout: ReturnType<typeof setTimeout>;

    const checkECSDKAvailability = (attempts = 0) => {
      if (isPlayerReady) return;

      if (window.ECSDK) {
        initializeWidget();
      } else if (attempts < 12) {
        setTimeout(() => checkECSDKAvailability(attempts + 1), 1000);
      } else {
        setError('SDK LOAD ERROR');
        setIsLoading(false);
      }
    };

    const initializeWidget = () => {
      setIsLoading(false);
      window.ECSDK.loadWidget('player', {
        partnerKey,
        partnerSdkToken: accessToken,
        dialogId,
        container,
        lang: languageCode,
        settings: {
          autoStart,
          isComprehensionQuizEnabled,
        },
      });

      readyHandlerTimeout = setTimeout(() => {
        if (!isPlayerReady) {
          setError('PLAYER LOAD ERROR');
          setIsLoading(false);
        }
      }, 35000);

      window.ECSDK.setOnReadyHandler(function(){
        isPlayerReady = true;
        clearTimeout(readyHandlerTimeout);
        setError(null);
      });
    };

    checkECSDKAvailability();

    return () => {
      clearTimeout(readyHandlerTimeout);
    };
  }, [partnerKey, accessToken, dialogId, container, languageCode, autoStart, isComprehensionQuizEnabled]);

  if (isLoading) {
    return <CircularProgress />;
  }

  const playerContainer = document.getElementById(container);
  if (error) {
    if (typeof window !== 'undefined' && window.Rollbar) {
      window.Rollbar.error(`ECSDK Player Error: ${error}`, {
        dialogId,
      });
    } else {
      console.log('error', error);
    }

    if (playerContainer) {
      playerContainer.style.display = 'none';
    }

    const errorMessage = "We had trouble loading the video player, but we're working to fix it asap. Please try again later.";
    return (
      <ErrorContainer>
        <Box component="img" src="/public/static/mimi.png" alt="Mimi" sx={{ height: 100, mb: 1.25 }} />
        <Typography variant="h5" color="text.primary" sx={{ mb: 1.25, fontWeight: 500 }}>
          Uh-oh!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </ErrorContainer>
    );
  }

  if (playerContainer) {
    playerContainer.style.display = 'block';
  }

  return <div id={container} />;
};
